<template>
  <div class="hello">
    <div class="main-content">
      <h1>{{ msg }}</h1>
      <p>
        JeepWarehouse.com is where the Jeep wave never ends. It's a place for Jeepers of all kinds—whether you're cruising the beach, hitting the trails, or just proud of your daily driver. Share photos of your Jeep, check out upcoming events and meetups, and connect with Jeep clubs from coast to coast. You can even explore a collection of YouTube Jeep channels packed with tips, stories, and adventures. JeepWarehouse.com is all about community, connection, and celebrating the Jeep life together.
      </p>

      <!-- Upcoming Events Section -->
      <section class="featured-section">
        <h2>Upcoming Events</h2>
        <div v-if="upcomingEvents.length > 0" class="upcoming-events">
          <div v-for="event in upcomingEvents.slice(0, 3)" :key="event.date + event.title" class="upcoming-event">
            <div class="event-date">{{ formatEventDate(event.date) }}</div>
            <div class="event-title">{{ event.title || event.desc }}</div>
            <div class="event-location">{{ event.city }}, {{ event.state }}</div>
          </div>
        </div>
        <div v-else class="no-events">
          <router-link to="/events">See the Event Calendar</router-link>
        </div>
      </section>

      <p>
        Ready to get started? Introduce yourself and showcase your amazing Jeep on our Spotlight page, 
        browse our carefully curated product recommendations to enhance your ride, 
        and don't forget to check out our Events Calendar to meet fellow Jeep enthusiasts in your area. 
        Adventure awaits - let's explore it together!
      </p>
    </div>

    <div class="sidebar">
      <!-- Featured Jeeps Section -->
      <section class="featured-section sidebar-section">
        <h2>Featured Jeeps</h2>
        <div class="featured-jeeps">
          <div v-for="jeep in featuredJeeps" :key="jeep.id" class="featured-jeep">
            <img :src="getJeepPhotoUrl(jeep.photoPath)" :alt="jeep.jeepName" class="featured-jeep-photo" />
            <div class="featured-jeep-info">
              <div class="featured-jeep-name">{{ jeep.jeepName }}</div>
              <div class="featured-jeep-location">{{ jeep.city }}, {{ jeep.state }}</div>
            </div>
          </div>
        </div>
      </section>

      <!-- Featured Products Section -->
      <section class="featured-section sidebar-section">
        <h2>Featured Products</h2>
        <div class="featured-products">
          <div v-for="product in featuredProducts" :key="product.name" class="featured-product">
            <img :src="product.image" :alt="product.name" class="featured-product-photo" />
            <div class="featured-product-info">
              <div class="featured-product-name">{{ product.name }}</div>
              <a :href="product.link" target="_blank" rel="noopener noreferrer" class="shop-link">Shop Now</a>
            </div>
          </div>
        </div>
        <router-link to="/spotlight" class="see-more-link">See more products</router-link>
      </section>

      <!-- Debug Link (temporary) -->
      <section class="debug-section">
        <router-link to="/upload-debug" class="debug-link">🔧 Upload Debug Page</router-link>
      </section>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';

const PRODUCT_LIST = [
  {
    name: "7\" Round LED Headlights for Jeep Wrangler",
    image: "/images/products/led-headlights.svg",
    link: "https://www.amazon.com/dp/example-link"
  },
  {
    name: "All-Weather Floor Mats for Jeep Wrangler",
    image: "/images/products/floor-mats.svg",
    link: "https://www.amazon.com/dp/example-link-2"
  },
  {
    name: "Complete Off-Road Recovery Kit",
    image: "/images/products/recovery-kit.svg",
    link: "https://www.amazon.com/dp/example-link-3"
  }
];

export default {
  name: 'HelloWorld',
  props: {
    msg: String
  },
  setup() {
    // --- Featured Jeeps ---
    const featuredJeeps = ref([]);
    function getJeepPhotoUrl(photoPath) {
      // Handle uploaded photos (unique filenames)
      if (photoPath && photoPath.startsWith('vehicle_')) {
        return `/data/images/${photoPath}`;
      }
      // Demo jeeps use SVGs, legacy uploaded jeeps use base64
      if (photoPath && photoPath.startsWith('data:')) return photoPath;
      const imageMap = {
        'jeep1.jpg': '/images/jeeps/jeep1.svg',
        'jeep2.jpg': '/images/jeeps/jeep2.svg',
        'jeep3.jpg': '/images/jeeps/jeep3.svg'
      };
      return imageMap[photoPath] || photoPath || 'https://via.placeholder.com/300x200/3a2c1a/bfa16c?text=Jeep+Photo';
    }
    function pickRandom(arr, n) {
      const copy = arr.slice();
      for (let i = copy.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
      }
      return copy.slice(0, n);
    }
    function loadJeeps() {
      let jeeps = [];
      try {
        const allJeeps = JSON.parse(localStorage.getItem('jwRides')) || [];
        // Filter out deleted vehicles
        jeeps = allJeeps.filter(jeep => !jeep.deleted);
      } catch (e) {
        console.warn('Failed to load jeeps from localStorage:', e);
        jeeps = [];
      }
      if (!jeeps.length) {
        // fallback to static JSON
        jeeps = [
          { id: 1, jeepName: 'Trail Blazer', city: 'Denver', state: 'CO', photoPath: 'jeep1.jpg' },
          { id: 2, jeepName: 'Desert Fox', city: 'Phoenix', state: 'AZ', photoPath: 'jeep2.jpg' },
          { id: 3, jeepName: 'Muddy Buddy', city: 'Houston', state: 'TX', photoPath: 'jeep3.jpg' }
        ];
      }
      featuredJeeps.value = pickRandom(jeeps, 2);
    }

    // --- Featured Products ---
    const featuredProducts = ref([]);
    function loadProducts() {
      featuredProducts.value = pickRandom(PRODUCT_LIST, 2);
    }

    // --- Upcoming Events ---
    const upcomingEvents = ref([]);
    function loadEvents() {
      let events = [];
      try {
        events = JSON.parse(localStorage.getItem('jwEventsCalendar')) || [];
      } catch (e) {
        console.warn('Failed to load events from localStorage:', e);
        events = [];
      }
      // Only show events with a date in the future
      const now = new Date();
      events = events.filter(ev => {
        if (!ev.date) return false;
        const evDate = new Date(ev.date);
        return evDate >= now;
      });
      // Sort by date ascending
      events.sort((a, b) => new Date(a.date) - new Date(b.date));
      upcomingEvents.value = events.slice(0, 5);
    }
    function formatEventDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    }

    onMounted(() => {
      loadJeeps();
      loadProducts();
      loadEvents();
    });

    return {
      featuredJeeps,
      getJeepPhotoUrl,
      featuredProducts,
      upcomingEvents,
      formatEventDate
    };
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
  .hello {
    max-width: 1200px;
    margin: 0 auto;
    padding: 2rem;
    text-align: center;
    display: grid;
    grid-template-columns: 1fr 300px;
    gap: 2rem;
    align-items: start;
  }
  .main-content {
    display: flex;
    flex-direction: column;
  }
  .sidebar {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }
  .hello h1 {
    font-family: 'Inter', sans-serif;
    font-weight: 600;
    color: #3a2c1a;
    margin-bottom: 2rem;
    font-size: 2.5rem;
    line-height: 1.2;
  }
  .hello p {
    font-family: 'Inter', sans-serif;
    font-weight: 400;
    font-size: 1.1rem;
    line-height: 1.7;
    color: #2c2c2c;
    text-align: left;
    max-width: 700px;
    margin: 0 auto 1.5rem auto;
  }
  .featured-section {
    margin: 2.5rem 0;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.07);
    padding: 2rem 1rem 1.5rem 1rem;
  }
  .featured-section h2 {
    color: #bfa16c;
    font-size: 1.5rem;
    margin-bottom: 1.2rem;
    font-weight: 600;
    letter-spacing: 1px;
  }
  .sidebar-section {
    margin: 0 0 1.5rem 0;
    padding: 1.5rem 1rem;
  }
  .sidebar-section h2 {
    font-size: 1.3rem;
    margin-bottom: 1rem;
  }
  .featured-jeeps, .featured-products {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 1rem;
  }
  .featured-jeep, .featured-product {
    background: #f4f1ed;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
    padding: 1rem;
    width: 100%;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .featured-jeep-photo {
    width: 150px;
    height: 100px;
    object-fit: contain;
    border-radius: 8px;
    margin-bottom: 0.5rem;
    background: #fff;
    box-shadow: 0 1px 4px rgba(0,0,0,0.07);
  }
  .featured-jeep-name {
    font-weight: 600;
    color: #3a2c1a;
    font-size: 1rem;
    margin-bottom: 0.2rem;
  }
  .featured-jeep-location {
    color: #666;
    font-size: 0.9rem;
  }
  .featured-product-photo {
    width: 80px;
    height: 60px;
    object-fit: contain;
    border-radius: 8px;
    margin-bottom: 0.5rem;
    background: #fff;
    box-shadow: 0 1px 4px rgba(0,0,0,0.07);
  }
  .featured-product-name {
    font-weight: 600;
    color: #3a2c1a;
    font-size: 0.95rem;
    margin-bottom: 0.3rem;
    line-height: 1.3;
  }
  .shop-link {
    color: #fff;
    background: #bfa16c;
    padding: 0.4rem 1rem;
    border-radius: 6px;
    text-decoration: none;
    font-weight: 500;
    font-size: 0.98rem;
    transition: background 0.2s;
    display: inline-block;
    margin-top: 0.2rem;
  }
  .shop-link:hover {
    background: #3a2c1a;
  }
  .see-more-link {
    display: inline-block;
    margin-top: 0.7rem;
    color: #bfa16c;
    font-weight: 600;
    text-decoration: underline;
    font-size: 1rem;
  }
  .see-more-link:hover {
    color: #3a2c1a;
  }
  .upcoming-events {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
    align-items: center;
    margin-bottom: 1rem;
  }
  .upcoming-event {
    background: #f4f1ed;
    border-radius: 8px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);
    padding: 1rem 1.5rem;
    min-width: 260px;
    max-width: 400px;
    text-align: left;
  }
  .event-date {
    color: #bfa16c;
    font-weight: 600;
    font-size: 1.05rem;
    margin-bottom: 0.2rem;
  }
  .event-title {
    color: #3a2c1a;
    font-weight: 500;
    font-size: 1.05rem;
    margin-bottom: 0.1rem;
  }
  .event-location {
    color: #666;
    font-size: 0.97rem;
  }
  .no-events {
    margin-top: 1rem;
    font-size: 1.05rem;
  }

  /* Responsive design */
  @media (max-width: 768px) {
    .hello {
      grid-template-columns: 1fr;
      gap: 1.5rem;
    }
    .sidebar {
      order: -1;
    }
    .sidebar-section {
      margin-bottom: 1rem;
    }
    .featured-jeeps, .featured-products {
      flex-direction: row;
      gap: 1rem;
      overflow-x: auto;
      padding-bottom: 0.5rem;
    }
    .featured-jeep, .featured-product {
      min-width: 140px;
      flex-shrink: 0;
    }
  }

.debug-section {
  text-align: center;
  margin-top: 2rem;
  padding: 1rem;
  background: #f8f6f3;
  border-radius: 8px;
  border: 1px solid #e0d5c7;
}

.debug-link {
  color: #d73527;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
  padding: 0.5rem 1rem;
  border: 1px solid #d73527;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.debug-link:hover {
  background: #d73527;
  color: white;
}
</style>
